import React, { useMemo, useState } from "react";
import CardHeader from "./common/CardHeader.jsx";
import Card from "./common/Card.jsx";
import useFetch from "../hooks/useFetch.js";
import { yearMonthDate } from "../helper/index.js";
import withLoadingError from "../helper/HOC/withLoadingError.js";
import StatusWidget from "./common/StatusWidget.jsx";
import DonutChart from "./common/DonutChart.jsx";
import FilterComponent from "./common/FilterComponent.jsx";
import { downloadReport } from "../services/index.js";

const lead_status_report_url = `${process.env.REACT_APP_API_URL}/api/online/v1/lead-status-report`;

const Composer = ({ data: dataArray, downloadRport }) => {
  const data = dataArray.reduce((prev, curr) => {
    return { ...prev, [curr.outlet__status]: curr.count };
  }, {});

  const config = [
    {
      title: "First Contact",
      info: data.first_contact_online ?? 0,
      icon: "rgba(0, 207, 255, 1)",
      status: "first_contact_online",
    },
    {
      title: "Docs Collected",
      info: data.doc_collection_online ?? 0,
      icon: "rgba(235, 7, 142, 1)",
      status: "doc_collection_online",
    },
    {
      title: "Follow-up",
      info: data.follow_up_online ?? 0,
      icon: "rgba(0, 102, 255, 1)",
      status: "follow_up_online",
    },
    {
      title: "Registered",
      info: data.registration_online ?? 0,
      icon: "rgba(234, 168, 110, 1)",
      status: "registration_online",
    },
    {
      title: "Meeting Scheduled",
      info: data.meeting_online ?? 0,
      icon: "rgba(54, 43, 115, 1)",
      status: "meeting_online",
    },
    {
      title: "M0/M1 Completed",
      info: data.checklist_online ?? 0,
      icon: "rgba(139, 177, 0, 1)",
      status: "checklist_online",
    },
    // {
    //   title: "New Acquisition Online",
    //   info: data.new_acquisition_online ?? 0,
    //   icon: "purple",
    //   status: "new_acquisition_online",
    // },
  ];

  function generateIndex(_idx, length) {
    const offsetFirstRow = Math.floor((length + 1) / 2);

    if (_idx % 2) {
      return offsetFirstRow + Math.floor((_idx + 1) / 2);
    }

    return Math.floor(_idx / 2 + 1);
  }

  const total = config.reduce((prev, curr) => prev + curr.info, 0);

  function generateDataList(config) {
    const result = config
      .map((element, _idx) => {
        const value = element.info;
        return {
          value: value,
          label: element.title,
          labelValue: element.title,
          color: element.icon,
          subLabelValue: `${value} out of ${total}`,
          id: generateIndex(_idx, config.length),
        };
      })
      .sort((a, b) => {
        if (a.id > b.id) return 1;
        return -1;
      });

    return total > 0
      ? result
      : [
          {
            value: 10,
            label: "Empty data",
            labelValue: "Empty data",
            color: "#E2136E",
            subLabelValue: "Empty data",
            id: -1,
          },
        ];
  }

  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <div>
        <DonutChart data={generateDataList(config)} />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap" }}>
        {config.map((e, _idx) => (
          <StatusWidget
            {...e}
            key={e.title}
            index={generateIndex(_idx, config.length)}
            download={true}
            downloadRport={downloadRport(e.status)}
          />
        ))}
      </div>
    </div>
  );
};

export default function LeadStatus({ dataStateMdo }) {
  const url = `${process.env.REACT_APP_API_URL}/api/online/v1/lead-status`;
  const [params, setParams] = useState({
    start_time: new Date(),
    end_time: new Date(),
  });

  const processdParams = useMemo(() => {
    const { start_time, end_time } = params;
    return {
      ...params,
      start_time: yearMonthDate(start_time),
      end_time: yearMonthDate(end_time),
    };
  }, [params]);

  const handleDownloadRport = (status) => () => {
    downloadReport(lead_status_report_url, { ...processdParams, status });
  };

  const dataState = useFetch(url, processdParams);
  const Overview = withLoadingError(Composer);

  const handleDateChange = (startDate, endDate) => {
    setParams({
      ...params,
      start_time: startDate,
      end_time: endDate,
    });
  };

  const handleParamChange = (id) => (event) => {
    setParams({
      ...params,
      [id]: event.target.value,
    });
  };

  const handleResetClick = () => {
    setParams({
      start_time: new Date(),
      end_time: new Date(),
    });
  };

  return (
    <Card>
      <CardHeader text="Lead Status" />
      <FilterComponent
        options={{ date: true, leadSource: true, mao: true, reset: true }}
        params={params}
        onParamChange={handleParamChange}
        onDateChange={handleDateChange}
        handleResetClick={handleResetClick}
        dataStateMdo={dataStateMdo}
      />
      <Overview {...dataState} downloadRport={handleDownloadRport} />
    </Card>
  );
}
